import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetAllPaymentGatewaysQuery,
  useUpdatePayeerConfigMutation,
  useUpdateCryptoConfigMutation,
  useUpdateSyriaTelConfigMutation,
  useUpdateShamCashConfigMutation,
  useUpdateBinanceConfigMutation,
  useUpdateCoinExConfigMutation,
} from '../../store/api/adminApi';

type GatewayType = 'payeer' | 'crypto' | 'syriatel' | 'shamCash' | 'binance' | 'coinex';

export function AdminPaymentGatewaysPage() {
  const { t } = useTranslation();
  const [editingGateway, setEditingGateway] = useState<GatewayType | null>(null);

  // Forms state
  const [payeerForm, setPayeerForm] = useState({
    accountNumber: '',
    merchantId: '',
    secretKey: '',
    minAmount: 0.1,
  });

  const [cryptoForm, setCryptoForm] = useState({
    walletAddress: '',
    platformName: '',
    merchantId: '',
    minAmount: 0.1,
  });

  const [syriatelForm, setSyriatelForm] = useState({
    receptionCodes: [''],
    minAmount: 0.1,
  });

  const [shamCashForm, setShamCashForm] = useState({
    receptionNumber: '',
    minAmount: 0.1,
  });

  const [binanceForm, setBinanceForm] = useState({
    enabled: false,
    apiKey: '',
    secretKey: '',
    walletAddress: '',
    minAmount: 1,
  });

  const [coinexForm, setCoinExForm] = useState({
    enabled: false,
    accessId: '',
    secretKey: '',
    adminEmail: '',
    coin: 'USDT',
    minAmount: 1,
  });

  const { data: gatewaysData, isLoading } = useGetAllPaymentGatewaysQuery();

  const [updatePayeer, { isLoading: isUpdatingPayeer }] = useUpdatePayeerConfigMutation();
  const [updateCrypto, { isLoading: isUpdatingCrypto }] = useUpdateCryptoConfigMutation();
  const [updateSyriaTel, { isLoading: isUpdatingSyriaTel }] = useUpdateSyriaTelConfigMutation();
  const [updateShamCash, { isLoading: isUpdatingShamCash }] = useUpdateShamCashConfigMutation();
  const [updateBinance, { isLoading: isUpdatingBinance }] = useUpdateBinanceConfigMutation();
  const [updateCoinEx, { isLoading: isUpdatingCoinEx }] = useUpdateCoinExConfigMutation();

  const handleStartEdit = (gatewayType: GatewayType) => {
    if (!gatewaysData?.data.configs) return;

    setEditingGateway(gatewayType);

    switch (gatewayType) {
      case 'payeer':
        setPayeerForm({
          accountNumber: gatewaysData.data.configs.payeer.accountNumber || '',
          merchantId: gatewaysData.data.configs.payeer.merchantId || '',
          secretKey: '', // لا نعرض secretKey الحالي لأسباب أمنية
          minAmount: gatewaysData.data.configs.payeer.minAmount ?? 0.1,
        });
        break;
      case 'crypto':
        setCryptoForm({
          walletAddress: gatewaysData.data.configs.crypto.walletAddress || '',
          platformName: gatewaysData.data.configs.crypto.platformName || '',
          merchantId: gatewaysData.data.configs.crypto.merchantId || '',
          minAmount: gatewaysData.data.configs.crypto.minAmount ?? 0.1,
        });
        break;
      case 'syriatel':
        setSyriatelForm({
          receptionCodes:
            gatewaysData.data.configs.syriatelCash.receptionCodes.length > 0
              ? [...gatewaysData.data.configs.syriatelCash.receptionCodes]
              : [''],
          minAmount: gatewaysData.data.configs.syriatelCash.minAmount ?? 0.1,
        });
        break;
      case 'shamCash':
        setShamCashForm({
          receptionNumber: gatewaysData.data.configs.shamCash.receptionNumber || '',
          minAmount: gatewaysData.data.configs.shamCash.minAmount ?? 0.1,
        });
        break;
      case 'binance':
        setBinanceForm({
          enabled: gatewaysData.data.configs.binance.enabled || false,
          apiKey: '',
          secretKey: '',
          walletAddress: gatewaysData.data.configs.binance.walletAddress || '',
          minAmount: gatewaysData.data.configs.binance.minAmount ?? 1,
        });
        break;
      case 'coinex':
        setCoinExForm({
          enabled: gatewaysData.data.configs.coinex.enabled || false,
          accessId: '',
          secretKey: '',
          adminEmail: gatewaysData.data.configs.coinex.adminEmail || '',
          coin: gatewaysData.data.configs.coinex.coin || 'USDT',
          minAmount: gatewaysData.data.configs.coinex.minAmount ?? 1,
        });
        break;
    }
  };

  const handleUpdatePayeer = async () => {
    try {
      await updatePayeer({
        accountNumber: payeerForm.accountNumber || null,
        merchantId: payeerForm.merchantId || null,
        secretKey: payeerForm.secretKey || null,
        minAmount: payeerForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.payeer.updateSuccess'));
      setEditingGateway(null);
      setPayeerForm({ accountNumber: '', merchantId: '', secretKey: '', minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.payeer.updateError')
      );
    }
  };

  const handleUpdateCrypto = async () => {
    try {
      await updateCrypto({
        walletAddress: cryptoForm.walletAddress || null,
        platformName: cryptoForm.platformName || null,
        merchantId: cryptoForm.merchantId || null,
        minAmount: cryptoForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.crypto.updateSuccess'));
      setEditingGateway(null);
      setCryptoForm({ walletAddress: '', platformName: '', merchantId: '', minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.crypto.updateError')
      );
    }
  };

  const handleUpdateSyriaTel = async () => {
    const codes = syriatelForm.receptionCodes.filter((code) => code.trim() !== '');
    try {
      await updateSyriaTel({
        receptionCodes: codes,
        minAmount: syriatelForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.syriatel.updateSuccess'));
      setEditingGateway(null);
      setSyriatelForm({ receptionCodes: [''], minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.syriatel.updateError')
      );
    }
  };

  const handleUpdateShamCash = async () => {
    try {
      await updateShamCash({
        receptionNumber: shamCashForm.receptionNumber || null,
        minAmount: shamCashForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.shamCash.updateSuccess'));
      setEditingGateway(null);
      setShamCashForm({ receptionNumber: '', minAmount: 0.1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.shamCash.updateError')
      );
    }
  };

  const handleUpdateBinance = async () => {
    try {
      await updateBinance({
        enabled: binanceForm.enabled,
        apiKey: binanceForm.apiKey || null,
        secretKey: binanceForm.secretKey || null,
        walletAddress: binanceForm.walletAddress || null,
        minAmount: binanceForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.binance.updateSuccess'));
      setEditingGateway(null);
      setBinanceForm({ enabled: false, apiKey: '', secretKey: '', walletAddress: '', minAmount: 1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.binance.updateError')
      );
    }
  };

  const handleUpdateCoinEx = async () => {
    try {
      await updateCoinEx({
        enabled: coinexForm.enabled,
        accessId: coinexForm.accessId || null,
        secretKey: coinexForm.secretKey || null,
        adminEmail: coinexForm.adminEmail || null,
        coin: coinexForm.coin,
        minAmount: coinexForm.minAmount,
      }).unwrap();
      toast.success(t('admin.paymentGateways.coinex.updateSuccess'));
      setEditingGateway(null);
      setCoinExForm({ enabled: false, accessId: '', secretKey: '', adminEmail: '', coin: 'USDT', minAmount: 1 });
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.paymentGateways.coinex.updateError')
      );
    }
  };

  const addSyriaTelCode = () => {
    setSyriatelForm({
      receptionCodes: [...syriatelForm.receptionCodes, ''],
    });
  };

  const removeSyriaTelCode = (index: number) => {
    const newCodes = syriatelForm.receptionCodes.filter((_, i) => i !== index);
    setSyriatelForm({
      receptionCodes: newCodes.length > 0 ? newCodes : [''],
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const configs = gatewaysData?.data.configs;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.paymentGateways.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {t('admin.paymentGateways.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payeer */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">Payeer</h2>
            {editingGateway !== 'payeer' && (
              <button
                onClick={() => handleStartEdit('payeer')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'payeer' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.accountNumber')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.accountNumber || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.merchantId')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.merchantId || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.secretKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.secretKey ? '••••••••' : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.payeer.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.payeer.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.accountNumber')}
                </label>
                <input
                  type="text"
                  value={payeerForm.accountNumber}
                  onChange={(e) => setPayeerForm({ ...payeerForm, accountNumber: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.accountNumberPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.merchantId')}
                </label>
                <input
                  type="text"
                  value={payeerForm.merchantId}
                  onChange={(e) => setPayeerForm({ ...payeerForm, merchantId: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.merchantIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.secretKey')}
                </label>
                <input
                  type="password"
                  value={payeerForm.secretKey}
                  onChange={(e) => setPayeerForm({ ...payeerForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.payeer.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.payeer.secretKeyNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.payeer.minAmount')}
                </label>
                <input
                  type="number"
                  value={payeerForm.minAmount}
                  onChange={(e) => setPayeerForm({ ...payeerForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdatePayeer}
                  disabled={isUpdatingPayeer}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingPayeer ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setPayeerForm({ accountNumber: '', merchantId: '', secretKey: '', minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Crypto */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.crypto.title')}
            </h2>
            {editingGateway !== 'crypto' && (
              <button
                onClick={() => handleStartEdit('crypto')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'crypto' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.walletAddress')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.crypto.walletAddress || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.platformName')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.platformName || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.merchantId')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.merchantId || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.crypto.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.crypto.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.walletAddress')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.walletAddress}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, walletAddress: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.walletAddressPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.platformName')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.platformName}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, platformName: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.platformNamePlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.merchantId')}
                </label>
                <input
                  type="text"
                  value={cryptoForm.merchantId}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, merchantId: e.target.value })}
                  placeholder={t('admin.paymentGateways.crypto.merchantIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.crypto.minAmount')}
                </label>
                <input
                  type="number"
                  value={cryptoForm.minAmount}
                  onChange={(e) => setCryptoForm({ ...cryptoForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCrypto}
                  disabled={isUpdatingCrypto}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCrypto ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setCryptoForm({ walletAddress: '', platformName: '', merchantId: '', minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SyriaTel */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.syriatel.title')}
            </h2>
            {editingGateway !== 'syriatel' && (
              <button
                onClick={() => handleStartEdit('syriatel')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'syriatel' ? (
            <div className="space-y-2">
              {configs?.syriatelCash.receptionCodes && configs.syriatelCash.receptionCodes.length > 0 ? (
                configs.syriatelCash.receptionCodes.map((code, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-neutral-medium font-body">
                      {t('admin.paymentGateways.syriatel.receptionCode')} {index + 1}:
                    </span>
                    <span className="text-neutral-light font-heading font-semibold">{code}</span>
                  </div>
                ))
              ) : (
                <p className="text-neutral-medium font-body">-</p>
              )}
              <div className="mt-2">
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.syriatel.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.syriatelCash.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.syriatel.receptionCodes')}
                </label>
                <div className="space-y-2">
                  {syriatelForm.receptionCodes.map((code, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => {
                          const newCodes = [...syriatelForm.receptionCodes];
                          newCodes[index] = e.target.value;
                          setSyriatelForm({ receptionCodes: newCodes });
                        }}
                        placeholder={t('admin.paymentGateways.syriatel.receptionCodePlaceholder')}
                        className="flex-1 px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                      />
                      {syriatelForm.receptionCodes.length > 1 && (
                        <button
                          onClick={() => removeSyriaTelCode(index)}
                          className="px-3 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-body"
                        >
                          {t('common.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addSyriaTelCode}
                  className="mt-2 px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                >
                  {t('admin.paymentGateways.syriatel.addCode')}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.syriatel.minAmount')}
                </label>
                <input
                  type="number"
                  value={syriatelForm.minAmount}
                  onChange={(e) => setSyriatelForm({ ...syriatelForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateSyriaTel}
                  disabled={isUpdatingSyriaTel}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingSyriaTel ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setSyriatelForm({ receptionCodes: [''], minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sham Cash */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.shamCash.title')}
            </h2>
            {editingGateway !== 'shamCash' && (
              <button
                onClick={() => handleStartEdit('shamCash')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'shamCash' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.receptionNumber')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.shamCash.receptionNumber || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.shamCash.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.shamCash.minAmount ?? 0.1} USD
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.receptionNumber')}
                </label>
                <input
                  type="text"
                  value={shamCashForm.receptionNumber}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, receptionNumber: e.target.value })}
                  placeholder={t('admin.paymentGateways.shamCash.receptionNumberPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.shamCash.minAmount')}
                </label>
                <input
                  type="number"
                  value={shamCashForm.minAmount}
                  onChange={(e) => setShamCashForm({ ...shamCashForm, minAmount: parseFloat(e.target.value) || 0 })}
                  placeholder="0.1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateShamCash}
                  disabled={isUpdatingShamCash}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingShamCash ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setShamCashForm({ receptionNumber: '', minAmount: 0.1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Binance */}
        <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.binance.title')}
            </h2>
            {editingGateway !== 'binance' && (
              <button
                onClick={() => handleStartEdit('binance')}
                className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'binance' ? (
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.enabled')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.enabled ? t('common.enabled') : t('common.disabled')}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.walletAddress')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold break-all">
                  {configs?.binance.walletAddress || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.minAmount')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.minAmount ?? 1} USD
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.apiKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.apiKey ? '••••••••' : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.secretKey')}:
                </span>
                <p className="text-neutral-light font-heading font-semibold">
                  {configs?.binance.secretKey ? '••••••••' : '-'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-neutral-medium font-body">
                  {t('admin.paymentGateways.binance.enabled')}
                </label>
                <input
                  type="checkbox"
                  checked={binanceForm.enabled}
                  onChange={(e) => setBinanceForm({ ...binanceForm, enabled: e.target.checked })}
                  className="w-5 h-5 rounded bg-neutral-dark border border-neutral-dark focus:ring-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.walletAddress')}
                </label>
                <input
                  type="text"
                  value={binanceForm.walletAddress}
                  onChange={(e) => setBinanceForm({ ...binanceForm, walletAddress: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.walletAddressPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.minAmount')}
                </label>
                <input
                  type="number"
                  value={binanceForm.minAmount}
                  onChange={(e) => setBinanceForm({ ...binanceForm, minAmount: e.target.value === '' ? 1 : parseFloat(e.target.value) || 0 })}
                  placeholder="1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.apiKey')}
                </label>
                <input
                  type="password"
                  value={binanceForm.apiKey}
                  onChange={(e) => setBinanceForm({ ...binanceForm, apiKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.apiKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.binance.apiKeyNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.binance.secretKey')}
                </label>
                <input
                  type="password"
                  value={binanceForm.secretKey}
                  onChange={(e) => setBinanceForm({ ...binanceForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.binance.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.binance.secretKeyNote')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateBinance}
                  disabled={isUpdatingBinance}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingBinance ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setBinanceForm({ enabled: false, apiKey: '', secretKey: '', walletAddress: '', minAmount: 1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* CoinEx Card — hidden (commented out for easy restore) */}
        {false && (
        <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-heading font-bold text-brand-accent">
              {t('admin.paymentGateways.coinex.title')}
            </h3>
            {editingGateway !== 'coinex' && (
              <button
                onClick={() => handleStartEdit('coinex')}
                className="px-4 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-all duration-300 font-heading font-semibold text-sm"
              >
                {t('admin.paymentGateways.edit')}
              </button>
            )}
          </div>

          {editingGateway !== 'coinex' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.enabled')}:</span>
                <span className={`ml-2 text-sm font-body ${gatewaysData?.data.configs.coinex.enabled ? 'text-green-400' : 'text-danger'}`}>
                  {gatewaysData?.data.configs.coinex.enabled ? t('common.yes') : t('common.no')}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.adminEmail')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body font-mono">
                  {gatewaysData?.data.configs.coinex.adminEmail || '-'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.coin')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.coin || 'USDT'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.minAmount')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.minAmount ?? 1}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.accessId')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.accessId ? '••••••••' : '-'}
                </span>
              </div>
              <div>
                <span className="text-sm text-neutral-medium font-body">{t('admin.paymentGateways.coinex.secretKey')}:</span>
                <span className="ml-2 text-sm text-neutral-light font-body">
                  {gatewaysData?.data.configs.coinex.secretKey ? '••••••••' : '-'}
                </span>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={coinexForm.enabled}
                    onChange={(e) => setCoinExForm({ ...coinexForm, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-neutral-dark rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand-accent"></div>
                </label>
                <span className="text-sm text-neutral-light font-body">
                  {t('admin.paymentGateways.coinex.enabled')}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.adminEmail')}
                </label>
                <input
                  type="email"
                  value={coinexForm.adminEmail}
                  onChange={(e) => setCoinExForm({ ...coinexForm, adminEmail: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.adminEmailPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.coin')}
                </label>
                <input
                  type="text"
                  value={coinexForm.coin}
                  onChange={(e) => setCoinExForm({ ...coinexForm, coin: e.target.value })}
                  placeholder="USDT"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.minAmount')}
                </label>
                <input
                  type="number"
                  value={coinexForm.minAmount}
                  onChange={(e) => setCoinExForm({ ...coinexForm, minAmount: e.target.value === '' ? 1 : parseFloat(e.target.value) || 0 })}
                  placeholder="1"
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.accessId')}
                </label>
                <input
                  type="password"
                  value={coinexForm.accessId}
                  onChange={(e) => setCoinExForm({ ...coinexForm, accessId: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.accessIdPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.coinex.accessIdNote')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                  {t('admin.paymentGateways.coinex.secretKey')}
                </label>
                <input
                  type="password"
                  value={coinexForm.secretKey}
                  onChange={(e) => setCoinExForm({ ...coinexForm, secretKey: e.target.value })}
                  placeholder={t('admin.paymentGateways.coinex.secretKeyPlaceholder')}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono text-sm"
                />
                <p className="text-xs text-neutral-medium font-body mt-1">
                  {t('admin.paymentGateways.coinex.secretKeyNote')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateCoinEx}
                  disabled={isUpdatingCoinEx}
                  className="flex-1 px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCoinEx ? (
                    <>
                      <ButtonSpinner size="xs" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    t('common.save')
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditingGateway(null);
                    setCoinExForm({ enabled: false, accessId: '', secretKey: '', adminEmail: '', coin: 'USDT', minAmount: 1 });
                  }}
                  className="flex-1 px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>)}
      </div>
    </div>
  );
}

